import { useState, useEffect } from 'react';
import styles from './header.module.scss';
import { Burger, NavLink, Drawer, Image, LoadingOverlay } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useRouter } from 'next/router';
import { categoryClicked } from "@/utils/event";
import SearchInput from '@/components/Search/search';
import { useSelector, useDispatch } from 'react-redux';
import { refreshGameList, recordCategoryClick } from '@/store/features/gameSlice';
import { STATIC_PAGES } from "@/const";
import RecentPlay from '@/components/RecentPlay/rencentPlay.jsx';

// 导航栏组件
const DesktopNav = ({ menuData, onNavigate, currentSlug }) => {
    return (
        <nav className={styles.menu_desktop}>
            {menuData.map((item, index) => (
                <a href={`/game/${item.path}`} onClick={(e) => {
                    e.preventDefault();
                    onNavigate(item);
                }} key={index} className={`${item.path === currentSlug ? styles.link_selected : ''}`}>
                    <span className={styles.nav_link}>
                        {item.name}
                    </span>
                    {item.children && item.children.length > 0 && (
                        <ul className={styles.submenu}>
                            {item.children.map((child, childIndex) => (
                                <li key={childIndex}>
                                    <a 
                                        href={`/${item.path}/${child.path}`}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            onNavigate(item);
                                        }}
                                        className={`${styles.submenu_link}`}
                                    >
                                        {child.name}
                                    </a>
                                </li>
                            ))}
                        </ul>
                    )}
                </a>
            ))}
        </nav>
    );
};

export default function Header({ menuData = [] }) {
    const router = useRouter();
    const dispatch = useDispatch();
    const { isMobile } = useSelector((state) => state.game);

    // 移动端菜单状态
    const [opened, { toggle, close }] = useDisclosure();

    // 菜单项
    const [currentMenu, setCurrentMenu] = useState({id: ""});
    const handleMenuClick = (menu) => {
        dispatch(refreshGameList());
        setCurrentMenu(menu);
        // 记录路由的点击到 Redux
        dispatch(recordCategoryClick({path: menu.path}));

        router.push({
            pathname: `/game/${menu.path}`,
        });
    };

    const handleLogoClick = () => {
        dispatch(refreshGameList());
        router.replace('/');
    };

    const handleBackHome = (link) => {
        router.push(`/${link}`);
        close();
    };

    useEffect(() => {
        // 等待路由准备就绪
        if (!router.isReady) return;

        const { slug } = router.query || {};

        // 设置当前菜单
        if (slug) {
            if (STATIC_PAGES.some((pagePath) => pagePath.includes(slug))) {
                setCurrentMenu({ path: slug, id: "" });
                dispatch(recordCategoryClick({path: slug}));
            }
        } else {
            setCurrentMenu({ path: "", id: "" });
        }

    }, [router.isReady, router.query]);
    
    return (
        <>
            {/* 顶部导航栏 */}
            <div className={styles.header}>
                <div className={styles.logo_menu}>
                    <img className={styles.logo} onClick={handleLogoClick} src="/images/icons/common/gameyeap_logo.svg" alt="gameyeap" />
                </div>

                {/* PC端菜单 */}
                {!isMobile && <DesktopNav menuData={menuData} onNavigate={handleMenuClick} currentSlug={router.query?.slug} />}

                {/* 移动端搜索 */}
                {isMobile && <SearchInput />}

                {/* 移动端菜单 */}
                { isMobile ? <div className={styles.menu_mobile}>
                    <Burger
                        opened={opened}
                        onClick={toggle}
                        styles={{
                            root: {
                                '--burger-color': '#431418'
                            }
                        }}
                        aria-label="Toggle navigation"
                    />
                    <Drawer
                        opened={opened}
                        onClose={close}
                        size="100%"
                        padding="md"
                        title="Navigation"
                        classNames={{
                            header: styles.drawer_header,
                            body: styles.drawer_body
                        }}
                        styles={{
                            content: {
                                background: '#ffdac3'
                            }
                        }}
                    >
                        {menuData.map((menu) => (
                            <NavLink
                                key={menu.id}
                                href={`/game/${menu.path}`}
                                label={menu.name}
                                leftSection={<Image src={menu.icon} alt={menu.name} width={32} height={32}/>}
                                childrenOffset={28}
                                classNames={{ section: styles.nav_link_section, root: `${styles.nav_link_root} ${menu.path === currentMenu.path ? styles.active : ''}` }}
                                onClick={() => {
                                    event.preventDefault();
                                    handleMenuClick(menu);
                                    close();
                                }}
                            >
                            {menu.children?.length > 0 && (
                                <div className={styles.nav_children}>
                                    {menu.children.map((child) => (
                                        <NavLink
                                            key={child.id}
                                            label={child.name}
                                            leftSection={<Image src="/static/icon/popcorn.png" width={32} height={32}/>}
                                            onClick={() => {
                                                handleMenuClick(child);
                                                close();
                                            }}
                                            classNames={{root: `${child.path === currentMenu.path ? styles.active : ''}`}}
                                        />
                                    ))}
                                </div>
                            )}
                            </NavLink>
                        ))}

                         <div className={`${styles.footer_bottom}`}>
                            <div className={styles.copyright}>
                                © 2025 Gameyeap.com LLC. All Rights Reserved.
                            </div>
                            <div className={styles.flex_row}>
                                <a 
                                    href="/about" 
                                    onClick={(e) => {
                                        e.preventDefault();
                                        handleBackHome("about");
                                    }} 
                                    className={styles.footer_link}
                                >
                                    About Us
                                </a>
                                <span className={styles.separator}>|</span>
                                <a 
                                    href="/contact" 
                                    onClick={(e) => {
                                        e.preventDefault();
                                        handleBackHome("contact");
                                    }} 
                                    className={styles.footer_link}
                                >
                                    Contact Us
                                </a>
                                <span className={styles.separator}>|</span>
                                <a 
                                    href="/privacy-policy" 
                                    onClick={(e) => {
                                        e.preventDefault();
                                        handleBackHome("privacy-policy");
                                    }} 
                                    className={[styles.footer_link]}
                                >
                                    Privacy Policy
                                </a>
                            </div>
                        </div>
                    </Drawer>
                </div> : <></>}

                {/* 搜索框 */}
                {!isMobile && <div className={styles.right_container}>
                    <SearchInput />
                    <RecentPlay />
                </div>}
            </div>
        </>
    );
}
